import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { FaMobileScreen } from "react-icons/fa6";
import LicenseIcon from "../../../assets/images/vectors/SideBarIcons/LicenseIcon";
import CommonButton from "../../../global/components/CommonButton";
import { formatLimitedMoney } from "../../../utils/limitedLicense";

/**
 * Summary before paying for a Limited License — styled like LicenseConfirmForm.
 */
export const LimitedLicenseConfirmForm = ({
  summary,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const oldRow = summary?.old;
  const nextRow = summary?.next;
  const payback = Number(summary?.payback ?? 0) || 0;
  const amountDue = Number(summary?.amountDue ?? 0) || 0;
  const compareMode = Boolean(oldRow);

  const buttonsData = [
    {
      label: "Apply",
      icon: <CheckCircleOutlined />,
      onClick: onConfirm,
      loading,
    },
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: onCancel,
    },
  ];

  const fmtMoney = (v) => {
    if (v === "-" || v === null || v === undefined || v === "") return "-";
    return formatLimitedMoney(v);
  };

  return (
    <div className="bg-white rounded-[4px] overflow-hidden flex flex-col max-h-[90vh]">
      <div className="py-2 bg-gray-200 py-1 px-2.5 rounded-[4px] flex items-center justify-between mb-1 flex-shrink-0">
        <div className="flex items-center">
          <LicenseIcon />
          <h2 className="text-sm font-[Poppins] text-black ml-2">
            Limited License Price
          </h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px] font-[Poppins]">
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "14%" }} />
            </colgroup>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700">
                  License Item
                </th>
                <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700">
                  Old License
                </th>
                <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700 whitespace-nowrap">
                  Old Price
                </th>
                <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700 whitespace-nowrap">
                  Old Amount
                </th>
                <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700">
                  New License
                </th>
                <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700 whitespace-nowrap">
                  New Price
                </th>
                <th className="px-2 py-1.5 text-left align-middle font-semibold text-gray-700 whitespace-nowrap">
                  New Amount
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200 hover:bg-gray-50">
                <td className="px-2 align-middle">
                  <div className="flex min-h-[28px] items-center gap-2.5">
                    <span className="inline-flex w-[18px] h-[18px] items-center justify-center shrink-0 text-gray-700">
                      <FaMobileScreen className="text-[15px]" />
                    </span>
                    <span className="min-w-0 flex-1 leading-snug text-gray-800 break-words">
                      Mobile Phone
                    </span>
                  </div>
                </td>
                <td className="px-2 align-middle leading-snug text-gray-700">
                  {compareMode ? oldRow.termLabel : "-"}
                </td>
                <td className="px-2 align-middle text-left leading-snug text-gray-600">
                  {compareMode ? fmtMoney(oldRow.price) : "-"}
                </td>
                <td className="px-2 align-middle text-left leading-snug text-gray-600">
                  {compareMode ? fmtMoney(oldRow.amount) : "-"}
                </td>
                <td className="px-2 align-middle font-medium leading-snug text-gray-900">
                  {nextRow?.termLabel || "-"}
                </td>
                <td className="px-2 align-middle text-left font-medium leading-snug text-gray-900">
                  {fmtMoney(nextRow?.price)}
                </td>
                <td className="px-2 align-middle text-left font-medium leading-snug text-gray-900">
                  {fmtMoney(nextRow?.amount)}
                </td>
              </tr>
            </tbody>
            <tfoot className="border-t-2 border-gray-300">
              {payback > 0 ? (
                <tr className="bg-gray-50">
                  <td className="px-2 pt-1 text-left align-middle font-semibold text-gray-800">
                    Payback
                  </td>
                  <td className="px-2 align-middle" />
                  <td className="px-2 align-middle" />
                  <td className="px-2 align-middle" />
                  <td className="px-2 align-middle" />
                  <td className="px-2 align-middle" />
                  <td className="px-2 text-left align-middle font-bold whitespace-nowrap text-red-600">
                    -{fmtMoney(payback)}
                  </td>
                </tr>
              ) : null}
              <tr className="bg-gray-50">
                <td className="px-2 py-1.5 text-left align-middle font-semibold text-gray-800">
                  Amount Due
                </td>
                <td className="px-2 py-1.5 align-middle" />
                <td className="px-2 py-1.5 align-middle" />
                <td className="px-2 py-1.5 align-middle" />
                <td className="px-2 py-1.5 align-middle" />
                <td className="px-2 py-1.5 align-middle" />
                <td className="px-2 py-1.5 text-left align-middle font-bold whitespace-nowrap text-gray-900">
                  {fmtMoney(amountDue)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-gray-200 bg-white pt-1">
        <div className="flex justify-center items-center py-1">
          <CommonButton buttonsData={buttonsData} />
        </div>
      </div>
    </div>
  );
};

export default LimitedLicenseConfirmForm;
